import React, { useEffect } from 'react';
import '../styling/forms.css';

const ImplantForm = ({ implantData, handleImplantChange, handleFileChange, handleFormSubmission }) => {

  useEffect(() => {
    handleImplantChange({
      target: {
        name: 'insertionDate',
        value: ''
      }
    });
    handleImplantChange({
      target: {
        name: 'timeToFailure',
        value: '' 
      }
    });
    window.scrollTo(0, 0);
  }, [handleImplantChange]);

  const calculateTimeToFailure = (insertionDate) => {
    if (!insertionDate) return;

    const currentDate = new Date();
    const insertionDateObj = new Date(insertionDate);
    const timeDiff = currentDate - insertionDateObj;

    const days = Math.floor(timeDiff / (1000 * 3600 * 24));
    let timeCategory = '';

    if (days <= 42) {
      timeCategory = '0-6 weeks';
    } else if (days <= 180) {
      timeCategory = '6 weeks - 6 months';
    } else if (days <= 2190) {
      timeCategory = '6 months - 6 years';
    } else {
      timeCategory = '6 years+';
    }

    handleImplantChange({
      target: {
        name: 'timeToFailure',
        value: timeCategory
      }
    });
  };

  const handleInsertionDateChange = (e) => {
    handleImplantChange(e);
    calculateTimeToFailure(e.target.value);
  };

  const renderCheckboxGroup = (label, name, options, isSingleSelection = false) => (
    <div className="form-section">
      <label>{label}</label>
      <div className="checkbox-group">
        {options.map((option) => (
          <label key={option}>
            <input
              type={isSingleSelection ? 'radio' : 'checkbox'}
              name={name}
              value={option}
              checked={isSingleSelection
                ? implantData[name] === option
                : implantData[name].includes(option)}
              onChange={(e) =>
                handleImplantChange({
                  target: {
                    name,
                    value: isSingleSelection
                      ? option
                      : e.target.checked
                        ? [...implantData[name], option]
                        : implantData[name].filter((item) => item !== option),
                  },
                })
              }
            />
            {option}
          </label>
        ))}
        {implantData[name]?.includes('Other') && (
          <div className="form-group">
            <input
              type="text"
              name={`${name}Other`}
              placeholder="Specify manufacturer"
              value={implantData[`${name}Other`] || ''}
              onChange={handleImplantChange}
              className="text-input"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderTextInputs = (fields) => (
    <div className="subfield-group">
      {fields.map(({ name, placeholder, type }) => (
        <div className="subfield" key={name}>
          <input
            type={type}
            name={name}
            value={implantData[name]}
            onChange={handleImplantChange}
            placeholder={placeholder}
            className="text-input"
            required
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="fill-form">
      <h1>Fill out the Dental Implant Form</h1>
      <form onSubmit={handleFormSubmission}>
        {renderCheckboxGroup('Implant Manufacturer:', 'manufacturer', [
          'Biohorizons', 'Nobel', 'Neodent', 'Straumann', 'Zimmer/ZimVie', 'Astra/Dentsply', 'BlueSky Bio', 'Noris', 'Other'
        ])}

        <div className="form-section">
          <label>Implant Product Info:</label>
          {renderTextInputs([
            { name: 'itemNumber', placeholder: 'Item Number', type: 'text' },
            { name: 'serialNumber', placeholder: 'Serial Number', type: 'text' },
            { name: 'lotNumber', placeholder: 'Lot Number', type: 'text' },
          ])}
        </div>

        <div className="form-section">
          <label>Insertion Date:</label>
          <input
            type="date"
            name="insertionDate"
            value={implantData.insertionDate}
            onChange={handleInsertionDateChange}
            className="text-input"
            required
          />
        </div>

        {renderCheckboxGroup('Time to Failure:', 'timeToFailure', [
          '0-6 weeks', '6 weeks - 6 months', '6 months - 6 years', '6 years+',
        ])}

        {renderCheckboxGroup('Insertion Torque:', 'insertionTorque', ['<20','20-30', '30-40', '>40'])}

        {renderCheckboxGroup('Context of Placement:', 'contextOfPlacement', [
          'Immediate', 'Delayed', 'Healed Ridge'
        ])}

        {renderCheckboxGroup('Implant Site Tissue Features:', 'tissueFeatures', [
          'Fenestration', 'Thread exposure', 'Dehiscence', 'Discolored Gingivae', 'Bone loss',
        ])}

        <div className="form-section">
          <label>Grafting:</label>
          {renderCheckboxGroup('Type of Graft:', 'graftingType', ['Allogeneic', 'Xenogeneic', 'Autograft'])}
          {renderCheckboxGroup('Membrane:', 'membrane', ['Yes', 'No'], true)}
          {implantData.membrane === 'Yes' && (
            <div className="form-group">
              <input
                type="text"
                name="membraneType"
                placeholder='Membrane Type'
                value={implantData.membraneType}
                onChange={handleImplantChange}
                className="text-input"
              />
            </div>
          )}
        </div>

        {renderCheckboxGroup('Healing:', 'healing', ['Cover Screw', 'Healing Abutment'])}

        <div className="form-section">
          <label>Patient Information:</label>
          {renderTextInputs([
            { name: 'age', placeholder: 'Age', type: 'number' },
            { name: 'medicalHistory', placeholder: 'Medical History', type: 'text' },
            { name: 'surgicalHistory', placeholder: 'Surgical History', type: 'text' },
            { name: 'radiation', placeholder: 'Radiation', type: 'text' },
            { name: 'chemotherapy', placeholder: 'Chemotherapy', type: 'text' },
            { name: 'medication', placeholder: 'Medication', type: 'text' },
            { name: 'tobaccoUse', placeholder: 'Tobacco use (amount and freq)', type: 'text' },
            { name: 'alcoholUse', placeholder: 'Alcohol use (amount and freq)', type: 'text' },
            { name: 'recreationalDrug', placeholder: 'Recreational Drug (amount and freq)', type: 'text' },
            { name: 'hygiene', placeholder: 'Hygiene', type: 'text' },
            { name: 'parafunctionalHabits', placeholder: 'Parafunctional Habits', type: 'text' },
            { name: 'periodontitis', placeholder: 'Periodontitis', type: 'text' },
            { name: 'gingivitis', placeholder: 'Gingivitis', type: 'text' },
          ])}
        </div>

        <div className="form-section">
          {renderCheckboxGroup('Restoring Practitioner:', 'restoringPractitioner', [
            'Occlusal interference', 'Restoration contours', 'Inappropriate torque',
            'Inadequate restorative space', 'Injudicious cementation', 'Prosthetic Restoration'
          ])}

          {implantData.restoringPractitioner.includes('Prosthetic Restoration') && (
            <div className="form-group">
              {renderCheckboxGroup('Prosthetic Restoration:', 'prostheticRestoration', [
                'Cement', 'Screw retained', 'Single', 'FPD', 'Full arch', 'Removable'
              ], true)}
            </div>
          )}
        </div>

        <div className="form-section">
          {renderCheckboxGroup('Surgeon:', 'surgeonIssues', [
            'Incorrect angulation', 'Incorrect depth', 'Damage to adjacent teeth', 'Osteomyelitis/Chronic infection',
            'Infrequent drill replacement', 'Over-torqued implant', 'Primary stability', 'Nerve Injury',
            'Inadequate keratinized tissue', 'Sinus perforation/exposure', 'Retained root tip/infection'
          ])}
        </div>

        <div className="form-section">
          {renderCheckboxGroup('Device:', 'deviceIssues', [
            'Implant Body Failure', 'Restorative Component Failure (abutment screw)', 'Implant Packaging Failure',
            'Visible Defect in Implant or Components'
          ])}
        </div>

        <div className="form-section">
          <label>Surgical Instrumentation:</label>
          <div className="subfield-group">
            {[
              { name: 'sterilizationMethod', placeholder: 'Method of Sterilization', type: 'text' },
              { name: 'cyclesBeforeReplacement', placeholder: 'Number of Cycles Before Drill Replacement', type: 'number' }
            ].map(({ name, placeholder, type }) => (
              <div className="subfield" key={name}>
                <input
                  type={type}
                  name={name}
                  value={implantData[name]}
                  onChange={handleImplantChange}
                  placeholder={placeholder}
                  className="text-input"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          {renderCheckboxGroup('Diagnosis at Failure:', 'diagnosisAtFailure', [
            'Peri-implantitis', 'Failure of osseointegration', 'Allergy to material',
            'Medication (Bisphosphonate, Chemotherapeutic, etc)', 'Radiation', 'Medical Compromise',
            'Abnormal Loading', 'Trauma', 'Nerve injury', 'Osteomyelitis'
          ])}
        </div>

        <div className="form-section">
          <label>Upload X-rays</label>
          <div className="checkbox-group">
            <div className="subfield">
              <label>Preplacement X-ray:</label>
              <input
                type="file"
                name="preplacementXray"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'preplacementXray')}
              />
            </div>
            <div className="subfield">
              <label>Removal X-ray:</label>
              <input
                type="file"
                name="removalXray"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'removalXray')}
              />
            </div>
          </div>
        </div>

        <button type="submit">Submit Form</button>
      </form>
    </div>
  );
};

export default ImplantForm;
